import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export type ThemeId = 
  | 'orange-red' | 'midnight-gold' | 'emerald-white' | 'rose-black' 
  | 'navy-coral' | 'purple-white' | 'teal-cream' | 'slate-orange'
  | 'indigo-pink' | 'forest-gold' | 'ocean-sand' | 'berry-cream'
  | 'charcoal-lime' | 'cobalt-silver' | 'mocha-cream' | 'ruby-black'
  | 'sapphire-gold' | 'mint-dark' | 'sunset-purple' | 'graphite-cyan';

export type NavbarStyle = 'transparent-scroll' | 'solid' | 'glass' | 'dark';

interface Theme {
  id: ThemeId;
  name: string;
  desc: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    bg: string;
    text: string;
  };
  preview: string;
}

interface Props {
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const THEMES: Theme[] = [
  {
    id: 'orange-red',
    name: 'Sunset Blaze',
    desc: 'Current theme — energetic orange to red gradient',
    colors: { primary: '#FF5C00', secondary: '#FF1744', accent: '#FF8A00', bg: '#FFFFFF', text: '#1F2937' },
    preview: 'linear-gradient(135deg, #FF5C00, #FF1744)',
  },
  {
    id: 'midnight-gold',
    name: 'Midnight Gold',
    desc: 'Luxury black with rich gold accents — premium feel',
    colors: { primary: '#0A0A0A', secondary: '#D4AF37', accent: '#FFD700', bg: '#0F0F0F', text: '#FFFFFF' },
    preview: 'linear-gradient(135deg, #0A0A0A, #D4AF37)',
  },
  {
    id: 'emerald-white',
    name: 'Emerald Fresh',
    desc: 'Clean green with white — natural organic vibe',
    colors: { primary: '#059669', secondary: '#10B981', accent: '#34D399', bg: '#FFFFFF', text: '#064E3B' },
    preview: 'linear-gradient(135deg, #059669, #10B981)',
  },
  {
    id: 'rose-black',
    name: 'Rose Noir',
    desc: 'Trendy pink with deep black — fashion forward',
    colors: { primary: '#BE185D', secondary: '#1F2937', accent: '#F472B6', bg: '#FFFFFF', text: '#1F2937' },
    preview: 'linear-gradient(135deg, #BE185D, #1F2937)',
  },
  {
    id: 'navy-coral',
    name: 'Nautical Coral',
    desc: 'Deep navy with warm coral — coastal elegance',
    colors: { primary: '#1E3A5F', secondary: '#FF6B6B', accent: '#4ECDC4', bg: '#F8FAFC', text: '#1E3A5F' },
    preview: 'linear-gradient(135deg, #1E3A5F, #FF6B6B)',
  },
  {
    id: 'purple-white',
    name: 'Royal Purple',
    desc: 'Regal purple with clean white — modern luxury',
    colors: { primary: '#7C3AED', secondary: '#A78BFA', accent: '#C4B5FD', bg: '#FFFFFF', text: '#5B21B6' },
    preview: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
  },
  {
    id: 'teal-cream',
    name: 'Teal Cream',
    desc: 'Sophisticated teal with warm cream — boutique feel',
    colors: { primary: '#0D9488', secondary: '#14B8A6', accent: '#FEF3C7', bg: '#FFFBEB', text: '#134E4A' },
    preview: 'linear-gradient(135deg, #0D9488, #14B8A6)',
  },
  {
    id: 'slate-orange',
    name: 'Slate Fire',
    desc: 'Cool slate with warm orange — balanced contrast',
    colors: { primary: '#475569', secondary: '#F97316', accent: '#FB923C', bg: '#F8FAFC', text: '#334155' },
    preview: 'linear-gradient(135deg, #475569, #F97316)',
  },
  {
    id: 'indigo-pink',
    name: 'Indigo Pop',
    desc: 'Deep indigo with hot pink — bold creative',
    colors: { primary: '#4338CA', secondary: '#EC4899', accent: '#F472B6', bg: '#FFFFFF', text: '#3730A3' },
    preview: 'linear-gradient(135deg, #4338CA, #EC4899)',
  },
  {
    id: 'forest-gold',
    name: 'Forest Gold',
    desc: 'Deep forest green with gold — premium nature',
    colors: { primary: '#14532D', secondary: '#166534', accent: '#D4AF37', bg: '#F0FDF4', text: '#14532D' },
    preview: 'linear-gradient(135deg, #14532D, #D4AF37)',
  },
  {
    id: 'ocean-sand',
    name: 'Ocean Sand',
    desc: 'Deep ocean blue with sandy beige — beach vibes',
    colors: { primary: '#0369A1', secondary: '#0EA5E9', accent: '#FDE68A', bg: '#FEFCE8', text: '#0C4A6E' },
    preview: 'linear-gradient(135deg, #0369A1, #0EA5E9)',
  },
  {
    id: 'berry-cream',
    name: 'Berry Cream',
    desc: 'Rich berry with soft cream — sweet elegant',
    colors: { primary: '#9F1239', secondary: '#E11D48', accent: '#FDE68A', bg: '#FFF1F2', text: '#881337' },
    preview: 'linear-gradient(135deg, #9F1239, #E11D48)',
  },
  {
    id: 'charcoal-lime',
    name: 'Charcoal Lime',
    desc: 'Dark charcoal with electric lime — edgy modern',
    colors: { primary: '#374151', secondary: '#1F2937', accent: '#A3E635', bg: '#111827', text: '#F3F4F6' },
    preview: 'linear-gradient(135deg, #374151, #A3E635)',
  },
  {
    id: 'cobalt-silver',
    name: 'Cobalt Silver',
    desc: 'Electric cobalt with silver — tech futuristic',
    colors: { primary: '#2563EB', secondary: '#3B82F6', accent: '#C0C0C0', bg: '#EFF6FF', text: '#1E40AF' },
    preview: 'linear-gradient(135deg, #2563EB, #3B82F6)',
  },
  {
    id: 'mocha-cream',
    name: 'Mocha Cream',
    desc: 'Rich coffee brown with cream — warm cozy',
    colors: { primary: '#78350F', secondary: '#92400E', accent: '#FEF3C7', bg: '#FFFBEB', text: '#451A03' },
    preview: 'linear-gradient(135deg, #78350F, #92400E)',
  },
  {
    id: 'ruby-black',
    name: 'Ruby Black',
    desc: 'Deep ruby red with black — dramatic luxury',
    colors: { primary: '#9F1239', secondary: '#000000', accent: '#F43F5E', bg: '#0A0A0A', text: '#FFFFFF' },
    preview: 'linear-gradient(135deg, #9F1239, #000000)',
  },
  {
    id: 'sapphire-gold',
    name: 'Sapphire Gold',
    desc: 'Royal blue with gold — premium jewelry feel',
    colors: { primary: '#1E40AF', secondary: '#3B82F6', accent: '#FFD700', bg: '#EFF6FF', text: '#1E3A8A' },
    preview: 'linear-gradient(135deg, #1E40AF, #FFD700)',
  },
  {
    id: 'mint-dark',
    name: 'Mint Dark',
    desc: 'Fresh mint with dark charcoal — modern clean',
    colors: { primary: '#14B8A6', secondary: '#0F766E', accent: '#5EEAD4', bg: '#134E4A', text: '#F0FDFA' },
    preview: 'linear-gradient(135deg, #14B8A6, #0F766E)',
  },
  {
    id: 'sunset-purple',
    name: 'Sunset Purple',
    desc: 'Warm sunset orange with purple — dreamy gradient',
    colors: { primary: '#EA580C', secondary: '#9333EA', accent: '#FBBF24', bg: '#FFF7ED', text: '#7C2D12' },
    preview: 'linear-gradient(135deg, #EA580C, #9333EA)',
  },
  {
    id: 'graphite-cyan',
    name: 'Graphite Cyan',
    desc: 'Dark graphite with bright cyan — tech sleek',
    colors: { primary: '#374151', secondary: '#4B5563', accent: '#06B6D4', bg: '#1F2937', text: '#F9FAFB' },
    preview: 'linear-gradient(135deg, #374151, #06B6D4)',
  },
];

const NAVBAR_STYLES: { id: NavbarStyle; name: string; desc: string; icon: string }[] = [
  { id: 'transparent-scroll', name: 'Transparent → Solid', desc: 'Hero pe transparent, scroll pe solid', icon: 'ri-contrast-line' },
  { id: 'solid', name: 'Always Solid', desc: 'Hamesha colored background', icon: 'ri-square-fill' },
  { id: 'glass', name: 'Glassmorphism', desc: 'Frosted glass blur effect', icon: 'ri-drop-line' },
  { id: 'dark', name: 'Always Dark', desc: 'Hamesha dark/black navbar', icon: 'ri-moon-line' },
];

export default function ThemePanel({ onToast }: Props) {
  const [currentTheme, setCurrentTheme] = useState<ThemeId>('orange-red');
  const [currentNavbar, setCurrentNavbar] = useState<NavbarStyle>('transparent-scroll');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    const { data, error } = await supabase
      .from('theme_settings')
      .select('theme_id, navbar_style')
      .eq('id', 'main')
      .maybeSingle();
    
    if (error) {
      onToast('Theme load nahi hua: ' + error.message, 'error');
      setLoading(false);
      return;
    }

    if (data) {
      setCurrentTheme(data.theme_id as ThemeId);
      setCurrentNavbar(data.navbar_style as NavbarStyle);
    }
    setLoading(false);
  };

  const saveTheme = async (themeId: ThemeId) => {
    setSaving(true);
    const { error } = await supabase
      .from('theme_settings')
      .upsert({
        id: 'main',
        theme_id: themeId,
        navbar_style: currentNavbar,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      onToast('Theme save nahi hua: ' + error.message, 'error');
    } else {
      setCurrentTheme(themeId);
      onToast(`"${THEMES.find(t => t.id === themeId)?.name}" theme apply ho gaya!`, 'success');
    }
    setSaving(false);
  };

  const saveNavbarStyle = async (style: NavbarStyle) => {
    setSaving(true);
    const { error } = await supabase
      .from('theme_settings')
      .upsert({
        id: 'main',
        theme_id: currentTheme,
        navbar_style: style,
        updated_at: new Date().toISOString(),
      });

    if (error) {
      onToast('Navbar style save nahi hua: ' + error.message, 'error');
    } else {
      setCurrentNavbar(style);
      onToast(`"${NAVBAR_STYLES.find(s => s.id === style)?.name}" navbar style apply ho gaya!`, 'success');
    }
    setSaving(false);
  };

  const currentThemeData = THEMES.find(t => t.id === currentTheme);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
            <i className="ri-palette-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Website Theme</p>
            <p className="text-gray-400 text-xs">20 premium themes — choose your brand style</p>
          </div>
        </div>
      </div>

      {/* Current Theme Preview */}
      <div 
        className="rounded-2xl p-5 text-white"
        style={{ background: currentThemeData?.preview }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <i className="ri-check-line text-white text-xl"></i>
          </div>
          <div>
            <p className="font-bold text-lg">{currentThemeData?.name}</p>
            <p className="text-white/80 text-sm">{currentThemeData?.desc}</p>
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          {Object.entries(currentThemeData?.colors || {}).map(([key, color]) => (
            <div key={key} className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-lg px-2 py-1">
              <div className="w-4 h-4 rounded-full border border-white/30" style={{ backgroundColor: color }}></div>
              <span className="text-xs font-medium capitalize">{key}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Navbar Style Selector */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
            <i className="ri-layout-top-line text-gray-600"></i>
          </div>
          <p className="text-gray-900 font-bold text-sm">Navbar Style</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {NAVBAR_STYLES.map(style => {
            const isActive = currentNavbar === style.id;
            return (
              <button
                key={style.id}
                onClick={() => saveNavbarStyle(style.id)}
                disabled={saving}
                className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                  isActive 
                    ? 'border-gray-900 bg-gray-900 text-white' 
                    : 'border-gray-100 hover:border-gray-300 bg-white text-gray-700'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <i className={`${style.icon} ${isActive ? 'text-white' : 'text-gray-500'}`}></i>
                  <span className="font-bold text-sm">{style.name}</span>
                </div>
                <p className={`text-xs ${isActive ? 'text-white/70' : 'text-gray-400'}`}>{style.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Theme Grid */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
            <i className="ri-color-filter-line text-gray-600"></i>
          </div>
          <p className="text-gray-900 font-bold text-sm">Choose Theme ({THEMES.length} options)</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {THEMES.map((theme, idx) => {
            const isActive = currentTheme === theme.id;
            return (
              <button
                key={theme.id}
                onClick={() => saveTheme(theme.id)}
                disabled={saving}
                className={`relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer group ${
                  isActive ? 'border-gray-900 ring-2 ring-gray-900/20' : 'border-gray-100 hover:border-gray-300'
                }`}
              >
                {/* Preview gradient */}
                <div 
                  className="h-20 w-full"
                  style={{ background: theme.preview }}
                ></div>
                
                {/* Info */}
                <div className="p-3 bg-white">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-gray-400">#{idx + 1}</span>
                    <p className="font-bold text-sm text-gray-900">{theme.name}</p>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">{theme.desc}</p>
                </div>

                {/* Active badge */}
                {isActive && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all"></div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Info */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center shrink-0">
          <i className="ri-lightbulb-flash-line text-amber-500 text-lg"></i>
        </div>
        <div>
          <p className="text-amber-800 font-bold text-sm">Pro Tip</p>
          <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
            Theme change turant website pe reflect hoga. Customers ko page refresh ki zaroorat nahi. 
            Dark themes (Midnight Gold, Ruby Black, Charcoal Lime) ke saath "Always Dark" navbar best lagta hai.
          </p>
        </div>
      </div>
    </div>
  );
}